"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useIssuesTableData = void 0;
var _react = require("react");
var _hooks = require("../../../state/redux/hooks");
var _sheets = require("../../../state/redux/ducks/sheets");
var _documents = require("../../../state/redux/ducks/documents");
var _pushpinHelpers = require("../../../utils/data/pushpinHelpers");
var _alloyProvider = require("@adsk/alloy-provider");
var _IssuePlacementContext = require("../../../IssuePlacementContext");
var _useReferencePickerPlugins = require("../../../input-field-components/ReferencePickerField/useReferencePickerPlugins");
var _documentHooks = require("../../../utils/hooks/documentHooks");
var _DeletedIssuesSpaceContext = require("../../../DeletedIssuesSpaceContext");
const useIssuesTableData = function (issues) {
  let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
    // associations may already be fetched with fetchIssuesAction,
    // so this only needs to be true when issue data wasn't fetched that way
    shouldFetchAssociations: true
  };
  const dispatch = (0, _hooks.useDispatch)();
  const allIssueUids = issues.map(issue => issue.id);
  const allIssueUidsKey = JSON.stringify(allIssueUids);
  const [fetchedIssueIds, setFetchedIssueIds] = (0, _react.useState)([]);
  const getPlacementLink = (0, _IssuePlacementContext.useIssuePlacement)();
  const referencePickerPlugins = (0, _useReferencePickerPlugins.useReferencePickerPlugins)();
  const isDeletedSpace = (0, _react.useContext)(_DeletedIssuesSpaceContext.DeletedIssuesSpaceContext);
  (0, _react.useEffect)(() => {
    if (!options.shouldFetchAssociations) {
      return;
    }
    // we want to fetch sheet association for only the new uids...
    // this is important in part because if too many uids are provided
    // to the sheet association search endpoint, it forces it to return paginated results
    // in a way that our code is not set up to handle... we must always only be searching for
    // fewer than 50 sheet associations at a time.
    const allIssuesToFetch = issues.filter(issue => !fetchedIssueIds.includes(issue.id));
    const issueUidsToFetch = allIssuesToFetch.map(issue => issue.id);
    setFetchedIssueIds(fetchedIssueIds.concat(issueUidsToFetch));
    dispatch((0, _sheets.fetchIssuesSheetAssociation)({
      issues: allIssuesToFetch,
      useCache: true,
      includeDeletedMarkups: isDeletedSpace
    }));
    dispatch((0, _documents.fetchIssuesDocumentAssociations)({
      issues,
      useCache: true
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allIssueUidsKey]);
  const productId = (0, _alloyProvider.useCurrentProductId)();
  const selectSheetAssociationByIssueId = (0, _hooks.useSelector)(_sheets.getSheetAssociationByIssueId);
  const selectSheetBySheetId = (0, _hooks.useSelector)(_sheets.getSheetBySheetId);
  const getDocumentByDocumentUrn = (0, _documentHooks.useGetDocumentByDocumentUrn)();
  const selectIsDocumentErroredByUrn = (0, _hooks.useSelector)(_documents.getIsDocumentErroredByUrn);
  const selectDocumentByDocumentVersionId = (0, _hooks.useSelector)(_documents.getDocumentByDocumentVersionId);
  const selectDocumentFetchErrorByDocVersionId = (0, _hooks.useSelector)(_documents.getErrorByDocumentVersionId);
  return issues.map(issue => {
    const sheetAssociation = selectSheetAssociationByIssueId(issue.id);
    // `sheetId` will be undefined if loading (otherwise it will be a string or null)
    let sheetId = undefined;
    if (sheetAssociation) {
      // if the surface type is a docuemnt, we handle that with different logic later.
      // ...this part is just responsible for makign sure that that we set the associated sheetId,
      // or if the association is invalid then we make sheetId be null instead of undefined.
      if (sheetAssociation.surfaceType === _sheets.SHEET_SURFACE_TYPE) {
        sheetId = sheetAssociation.sheetId;
      } else if (!_sheets.VALID_SURFACE_TYPES.includes(sheetAssociation.surfaceType)) {
        sheetId = null;
      }
    }
    if (sheetAssociation === null) {
      sheetId = null;
    }
    // So with the code below, `sheet` will be either a sheet object, null, or undefined.
    // If it is undefined it means it is still loading, and we will show a spinner in the cell.
    const sheet = sheetId ? selectSheetBySheetId(sheetId) : sheetId;

    // `documentUrn` will be undefined if loading (otherwise it will be a string or null)
    const documentUrn = (0, _documentHooks.getDocumentUrnOfIssue)(issue);
    // Follows the same logic as sheet above.
    let documentEntity = documentUrn ? getDocumentByDocumentUrn(documentUrn) : documentUrn;
    let isErrorInDocumentSelection = false;
    // if we don't find a document by URN, it's possible that we're linking to the document by version ID
    if (!documentEntity && sheetAssociation && sheetAssociation.surfaceType === _sheets.DOCUMENT_SURFACE_TYPE) {
      // we don't need the whole error object, we just need to know if there is one
      isErrorInDocumentSelection = !!selectDocumentFetchErrorByDocVersionId(sheetAssociation.documentVersionId);
      documentEntity = selectDocumentByDocumentVersionId(sheetAssociation.documentVersionId);
    }
    if (documentEntity === undefined && documentUrn && !sheetAssociation) {
      isErrorInDocumentSelection = selectIsDocumentErroredByUrn(documentUrn);
    }

    // setting documentEntity to null when there is an error stops the loading spinny
    // (without this, documentEntity is undefined which causes the spinny)
    // ... usually the error is because the document on which the issue was was placed got deleted.
    if (isErrorInDocumentSelection) {
      documentEntity = null;
    }
    const hasLinkedIssueDocument = !!((0, _pushpinHelpers.hasLinkedDocument)(issue) || documentEntity && !sheet || isErrorInDocumentSelection);
    return {
      ...issue,
      placement: !!sheet ? sheet : documentEntity,
      references: {
        plugins: referencePickerPlugins
      },
      isDocumentPlacement: hasLinkedIssueDocument,
      isSheetPlacement: !!sheet,
      productId,
      placementLinkFunction: getPlacementLink
    };
  });
};
exports.useIssuesTableData = useIssuesTableData;
//# sourceMappingURL=useIssuesTableData.js.map